import { imageService } from "@repo/ui/lib/services/index";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ConfirmationDialog } from "../../components/ui/data-table/confirmation-dialog";
import { StatusBadge } from "../../components/ui/data-table/status-badge";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import { AuthService } from "../../lib/api/services/auth.service";
import {
  useActivateUser,
  useDeactivateUser,
  useDeleteUser,
  useResiliateUser,
  useUser,
} from "../../hooks/api/user";
import { ChangeProfileDialog } from "./change-profile-dialog";

interface UserDetailProps {
  userId: number;
  onBack: () => void;
  onEdit: () => void;
}

function formatDate(date: Date | null | undefined) {
  if (!date) return "Non disponible";
  return new Date(date).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function UserDetail({ userId, onBack, onEdit }: UserDetailProps) {
  const { data, isLoading, error } = useUser(userId);
  const deleteUser = useDeleteUser();
  const resiliateUser = useResiliateUser();
  const activateUser = useActivateUser();
  const deactivateUser = useDeactivateUser();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResiliationDialog, setShowResiliationDialog] = useState(false);
  const [showToggleActiveDialog, setShowToggleActiveDialog] = useState(false);
  const [showChangeProfileDialog, setShowChangeProfileDialog] = useState(false);
  const isAdmin = new AuthService().isAdmin();

  const handleDelete = () => {
    deleteUser.mutate(userId, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        onBack();
      },
    });
  };

  const handleToggleActive = () => {
    if (!data) return;
    const mutation = data.data.is_active ? deactivateUser : activateUser;
    mutation.mutate(userId, {
      onSuccess: () => setShowToggleActiveDialog(false),
    });
  };

  const handleResiliation = () => {
    resiliateUser.mutate(userId, {
      onSuccess: (response) => {
        setShowResiliationDialog(false);
        const summary = response.data;
        if (summary.alreadyProcessed) {
          toast.info("Ce compte était déjà résilié.");
        } else {
          toast.success("Compte résilié avec succès", {
            description: `${summary.usersSoftDeleted} utilisateur(s), ${summary.inventairesSoftDeleted} inventaire(s) marqués supprimés${summary.subscriptionCancelled ? " — abonnement Stripe annulé" : ""}.`,
          });
        }
        onBack();
      },
      onError: (error) => {
        toast.error("Échec de la résiliation", {
          description: error.message,
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6 animate-in fade-in-0 duration-200">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 w-24" />
        </div>
        <div className="bg-background rounded-lg shadow-sm border p-6">
          <div className="flex items-start gap-6">
            <Skeleton className="h-20 w-20 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
        </div>
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
          <p className="text-destructive mb-4">
            Une erreur s'est produite lors du chargement des détails de l'utilisateur.
          </p>
          <Button onClick={onBack} variant="outline">
            Retour
          </Button>
        </div>
      </div>
    );
  }

  const user = data.data;
  const getInitials = () => {
    const first = user.prenom || "";
    const last = user.nom || "";
    return (first.charAt(0) + last.charAt(0)).toUpperCase();
  };
  const isUserDisabled = !!user.deleted;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6 animate-in fade-in-0 duration-300">
      <div className="flex items-center justify-between">
        <Button onClick={onBack} variant="ghost">
          <ArrowLeft className="mr-1 h-4 w-4" /> Retour
        </Button>
        {isAdmin && (
          <Button onClick={onEdit}>Modifier</Button>
        )}
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <Avatar className="h-20 w-20 flex-shrink-0 ring-2 ring-border">
              <AvatarImage
                src={imageService.getImageUrl(user.img, "avatar")}
                alt={`${user.prenom} ${user.nom}`}
              />
              <AvatarFallback className="text-lg font-semibold">
                {getInitials()}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="text-2xl font-bold">
                  {user.prenom} {user.nom}
                </h1>
                <div className="flex gap-2">
                  {user.deleted ? (
                    <StatusBadge tone="deleted" label="Supprimé" />
                  ) : user.is_active ? (
                    <StatusBadge tone="active" label="Actif" />
                  ) : (
                    <StatusBadge tone="inactive" label="Inactif" />
                  )}
                </div>
              </div>

              <div className="space-y-1 text-muted-foreground">
                <p className="text-sm">@{user.username || "Aucun nom d'utilisateur"}</p>
                <p className="text-sm">{user.email}</p>
                {user.groupe?.name && (
                  <p className="text-sm">
                    Groupe:{" "}
                    <span className="font-medium text-foreground">
                      {user.groupe.name}
                    </span>
                  </p>
                )}
                {user.profil?.name && (
                  <p className="text-sm">
                    Profil:{" "}
                    <span className="font-medium text-foreground">
                      {user.profil.name}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isAdmin && (
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => setShowChangeProfileDialog(true)}
            variant="outline"
            disabled={isUserDisabled}
          >
            Changer le profil
          </Button>
          <Button
            onClick={() => setShowToggleActiveDialog(true)}
            variant="outline"
            disabled={isUserDisabled}
          >
            {user.is_active ? "Désactiver" : "Activer"}
          </Button>
          <Button
            onClick={() => setShowDeleteDialog(true)}
            variant="outline"
            disabled={isUserDisabled}
            className="text-destructive hover:text-destructive"
          >
            Supprimer
          </Button>
          <Button
            onClick={() => setShowResiliationDialog(true)}
            variant="destructive"
            disabled={isUserDisabled}
          >
            <ShieldAlert className="mr-1.5 h-4 w-4" />
            Résilier le compte
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Email vérifié
              </Label>
              <p className="text-sm mt-1">{user.email_active ? "Oui" : "Non"}</p>
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Téléphone
              </Label>
              <p className="text-sm mt-1">{user.phone_number || "Non renseigné"}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Adresse</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Ville
              </Label>
              <p className="text-sm mt-1">{user.city || "Non renseigné"}</p>
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Code postal
              </Label>
              <p className="text-sm mt-1">{user.zip_code || "Non renseigné"}</p>
            </div>
            {user.address && (
              <div>
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Adresse
                </Label>
                <p className="text-sm mt-1">{user.address}</p>
                {user.address2 && (
                  <p className="text-sm text-muted-foreground">{user.address2}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Historique</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Créé le
              </Label>
              <p className="text-sm mt-1">{formatDate(user.created_at as Date)}</p>
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Modifié le
              </Label>
              <p className="text-sm mt-1">{formatDate(user.updated_at as Date)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <ConfirmationDialog
        open={showResiliationDialog}
        title="Résilier le compte"
        description={`Cette action va annuler l'abonnement Stripe (si actif), marquer ${user.prenom} ${user.nom} et les autres utilisateurs du groupe comme supprimés, et soft-delete tous les inventaires liés. Cette opération est irréversible côté accès utilisateur.`}
        confirmLabel="Confirmer la résiliation"
        variant="destructive"
        isLoading={resiliateUser.isPending}
        onConfirm={handleResiliation}
        onCancel={() => setShowResiliationDialog(false)}
      />

      <ConfirmationDialog
        open={showDeleteDialog}
        title="Supprimer l'utilisateur"
        description={`Êtes-vous sûr de vouloir supprimer ${user.prenom} ${user.nom} ? Cette action ne peut pas être annulée.`}
        confirmLabel="Supprimer"
        variant="destructive"
        isLoading={deleteUser.isPending}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />

      <ConfirmationDialog
        open={showToggleActiveDialog}
        title={
          user.is_active ? "Désactiver l'utilisateur" : "Activer l'utilisateur"
        }
        description={
          user.is_active
            ? `${user.prenom} ${user.nom} ne pourra plus se connecter au système.`
            : `${user.prenom} ${user.nom} pourra à nouveau se connecter au système.`
        }
        confirmLabel={user.is_active ? "Désactiver" : "Activer"}
        variant={user.is_active ? "warning" : "default"}
        isLoading={activateUser.isPending || deactivateUser.isPending}
        onConfirm={handleToggleActive}
        onCancel={() => setShowToggleActiveDialog(false)}
      />

      <ChangeProfileDialog
        userId={userId}
        currentProfileId={user.profil?.id}
        isOpen={showChangeProfileDialog}
        onClose={() => setShowChangeProfileDialog(false)}
        onSuccess={() => {}}
      />
    </div>
  );
}
